import {
  ValidationOptions,
  TextResponse,
  NumberResponse,
  ChoicesResponse,
  BooleanResponse,
} from "@neuromotion-inc/mightier-survey-engine";
import type { TFunction } from "i18next";

export const validateTextInput = (
  response: TextResponse | null,
  rules: ValidationOptions<"TEXT">,
  t: TFunction,
): string[] => {
  if (!response) {
    return [];
  }

  if (response.type !== "TEXT") {
    throw new Error("Invalid response type for text input validation.");
  }

  const { responseValue: value } = response;

  const errs: string[] = [];

  if (
    rules.required &&
    (value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0))
  ) {
    errs.push(t("survey.validationMessages.fieldRequired"));
  }

  if (rules.max_length && value.length > rules.max_length) {
    errs.push(
      t("survey.validationMessages.textMaxLength", {
        maxLength: rules.max_length,
      }),
    );
  }

  if (rules.min_length && value.length < rules.min_length) {
    errs.push(
      t("survey.validationMessages.textMinLength", {
        minLength: rules.min_length,
      }),
    );
  }

  if (rules.isEmail && !value.includes("@")) {
    errs.push(t("survey.validationMessages.validEmail"));
  }

  if (rules.regex && !new RegExp(rules.regex).test(value)) {
    errs.push(t("survey.validationMessages.invalidFormat"));
  }

  return errs;
};

export const validateNumberInput = (
  response: NumberResponse | null,
  rules: ValidationOptions<"NUMBER">,
  t: TFunction,
): string[] => {
  if (!response) {
    return [];
  }

  const { responseValue: value } = response;
  const errors: string[] = [];

  if (rules.required && value === null) {
    errors.push(t("survey.validationMessages.fieldRequired"));
  }

  if (isNaN(value)) {
    errors.push(t("survey.validationMessages.textMustBeNumber"));
  }

  if (rules.min && value < rules.min) {
    errors.push(t("survey.validationMessages.numberMin", { min: rules.min }));
  }

  if (rules.max && value > rules.max) {
    errors.push(t("survey.validationMessages.numberMax", { max: rules.max }));
  }

  return errors;
};

export const validateChoicesInput = (
  response: ChoicesResponse | null,
  rules: ValidationOptions<"SINGLE_SELECT" | "MULTI_SELECT">,
  t: TFunction,
): string[] => {
  if (!response) {
    return [];
  }

  const { responseValue: value } = response;

  const errs: string[] = [];

  if (
    rules.required &&
    (value === null || (Array.isArray(value) && value.length === 0))
  ) {
    errs.push(t("survey.validationMessages.fieldRequired"));
  }

  if (rules.min_num_selections && value?.length < rules.min_num_selections) {
    errs.push(
      t("survey.validationMessages.choicesMinSelections", {
        minSelections: rules.min_num_selections,
      }),
    );
  }

  if (rules.max_num_selections && value?.length > rules.max_num_selections) {
    errs.push(
      t("survey.validationMessages.choicesMaxSelections", {
        maxSelections: rules.max_num_selections,
      }),
    );
  }

  return errs;
};

export const validateBooleanInput = (
  response: BooleanResponse | null,
  rules: ValidationOptions<"BOOLEAN">,
  t: TFunction,
): string[] => {
  if (!response) {
    return [];
  }

  const { responseValue: value } = response;

  const errs: string[] = [];

  if (
    rules.required &&
    (value === null || (Array.isArray(value) && value.length === 0))
  ) {
    errs.push(t("survey.validationMessages.fieldRequired"));
  }

  return errs;
};
